import { CircularProgress } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IEntityRelationsRow, PLURAL_KEY } from "@ryde/shared";
import { deepPurpleMui } from "@ryde/theme";

import { RelatedEntitiesRowIds } from "./related-entities-row-ids";
import {
  RELATED_ENTITIES_COUNT_SEPARATOR,
  RELATED_ENTITIES_DRAWER_OPEN_MS,
} from "./related-entities.constants";
import { RelatedEntitiesTableStyled } from "./related-entities.styled";

interface IRelatedEntitiesTable {
  rows: IEntityRelationsRow[];
  isLoading: boolean;
  onEntityClick: () => void;
}

export const RelatedEntitiesTable: FC<IRelatedEntitiesTable> = ({
  rows,
  isLoading,
  onEntityClick,
}) => {
  const { t } = useTranslation();
  const [isOpenAnimationDone, setIsOpenAnimationDone] = useState(false);

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setIsOpenAnimationDone(true);
    }, RELATED_ENTITIES_DRAWER_OPEN_MS);

    return () => window.clearTimeout(timerId);
  }, []);

  if (isLoading && !rows.length) {
    if (!isOpenAnimationDone) {
      return <RelatedEntitiesTableStyled aria-busy="true" />;
    }

    return (
      <RelatedEntitiesTableStyled>
        <div className="related-entities-loading" role="status">
          <CircularProgress
            size={40}
            thickness={4}
            sx={{ color: deepPurpleMui.A400 }}
          />
          <p className="related-entities-loading-caption">
            {t("ui.LOADING", { defaultValue: "Loading..." })}
          </p>
        </div>
      </RelatedEntitiesTableStyled>
    );
  }

  if (!rows.length) {
    return (
      <RelatedEntitiesTableStyled>
        <p className="related-entities-empty" role="status">
          {t("general.NO_RELATED_ENTITIES", {
            defaultValue: "No related entities found.",
          })}
        </p>
      </RelatedEntitiesTableStyled>
    );
  }

  return (
    <RelatedEntitiesTableStyled>
      <table className="related-entities-table-inner">
        <thead>
          <tr>
            <th className="entity-type-col">{t("general.RELATION_ENTITY")}</th>
            <th className="relation-ids-col">{t("general.RELATION_IDS")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.relation}>
              <td className="entity-type-cell">
                {t(`${row.relation}.${PLURAL_KEY}`)}
                <span className="entity-count-sep">
                  {" "}
                  {RELATED_ENTITIES_COUNT_SEPARATOR}{" "}
                </span>
                <span className="entity-count">{row.ids.length}</span>
              </td>
              <RelatedEntitiesRowIds
                relation={row.relation}
                ids={row.ids}
                onEntityClick={onEntityClick}
              />
            </tr>
          ))}
        </tbody>
      </table>
    </RelatedEntitiesTableStyled>
  );
};
